import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

interface Tag {
  id: string;
  name: string;
  description: string | null;
  type: string;
  color: string;
  icon?: string;
  created_at: string;
  criteria: any;
}

interface ResourceCounts {
  devices: number;
  profiles: number;
  applications: number;
  configurations: number;
  patchPolicies: number;
  users: number;
}

export const useTagDetail = (tagId: string) => {
  const [tag, setTag] = useState<Tag | null>(null);
  const [resourceCounts, setResourceCounts] = useState<ResourceCounts>({
    devices: 0,
    profiles: 0,
    applications: 0,
    configurations: 0,
    patchPolicies: 0,
    users: 0
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchTagDetail = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // Fetch tag details
        const { data: tagData, error: tagError } = await supabase
          .from('tags')
          .select('*')
          .eq('id', tagId)
          .single();

        if (tagError) {
          setError(tagError.message);
          return;
        }

        setTag(tagData);

        // Fetch resource counts in parallel
        const [deviceCount, profileCount, applicationCount, configurationCount, patchPolicyCount, userCount] = await Promise.all([
          // Count devices
          supabase
            .from('device_tags')
            .select('id', { count: 'exact' })
            .eq('tag_id', tagId),
          
          // Count profiles
          supabase
            .from('profile_tags')
            .select('id', { count: 'exact' })
            .eq('tag_id', tagId),
          
          // Count applications
          supabase
            .from('application_tags')
            .select('id', { count: 'exact' })
            .eq('tag_id', tagId),
          
          // Count configurations
          supabase
            .from('configuration_tags')
            .select('id', { count: 'exact' })
            .eq('tag_id', tagId),
          
          // Count patch policies
          supabase
            .from('patch_policy_tags')
            .select('id', { count: 'exact' })
            .eq('tag_id', tagId),
          
          // Count users
          supabase
            .from('device_user_tags')
            .select('id', { count: 'exact' })
            .eq('tag_id', tagId)
        ]);

        setResourceCounts({
          devices: deviceCount.count || 0,
          profiles: profileCount.count || 0,
          applications: applicationCount.count || 0,
          configurations: configurationCount.count || 0,
          patchPolicies: patchPolicyCount.count || 0,
          users: userCount.count || 0
        });

      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
      } finally {
        setIsLoading(false);
      }
  };

  useEffect(() => {
    if (tagId) {
      fetchTagDetail();
    }
  }, [tagId]);

  const refetch = () => {
    if (tagId) {
      fetchTagDetail();
    }
  };

  return { tag, resourceCounts, isLoading, error, refetch };
};